import { useState } from 'react'
import { useAtom } from 'jotai'
import Button from '@mui/material/Button'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'

import type { FeePayersGenerationResponse } from '@/types'
import { feePayersGenerationIdAtom } from '@/state'
import { useGenerateFeePayers } from '@/hooks/api/use-yearly-fee-collection'

export default function FeePayersGeneration({
    yearlyFeeCollectionId,
}: {
    yearlyFeeCollectionId: number,
}) {

    const [feePayersGenerationId, setFeePayersGenerationId] = useAtom(feePayersGenerationIdAtom)
    const [requestInProgress, setRequestInProgress] = useState<boolean>(false)

    // Action dialogs
    const [confirm, setConfirm] = useState<boolean>(false)

    const successCallback = (data: FeePayersGenerationResponse) => {
        if (data.id) {
            setFeePayersGenerationId(data.id)
        }
    }

    const mutation = useGenerateFeePayers(
        yearlyFeeCollectionId,
        {
            onSuccessCallback: successCallback,
            setRequestInProgress,
        },
    )

    const handleClickGenerate = () => {
        setRequestInProgress(true)
        mutation.mutate()
        setConfirm(false)
    }

    return (
        <>
            <Button
                color="terciary"
                variant="outlined"
                startIcon={<GroupsRoundedIcon />}
                onClick={() => setConfirm(true)}
                disabled={requestInProgress || !!feePayersGenerationId}
                sx={{
                    backgroundColor: 'errorRedLight.light',
                    borderColor: 'errorRedLight.dark',
                }}
            >
                1. Vygenerovat poplatníky
            </Button>
            <Dialog
                open={confirm}
                onClose={() => setConfirm(false)}
            >
                <DialogContent>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}>
                        <Typography variant="h5" component="div" mb={2}>
                            Potvrďte prosím vygenerování poplatníků
                        </Typography>
                        <Typography variant="body2" component="div" mb={2}>
                            Tato akce projde všechna svozová místa v obci a vygeneruje k nim poplatníky na základě dat z EOKO. 
                            Pro větší obce může trvat i desítky minut.
                        </Typography>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            flexGrow: 1,
                        }}>
                            <Button
                                variant="text"
                                color="terciary"
                                onClick={() => setConfirm(false)}
                            >
                                Zrušit
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={requestInProgress ? <CircularProgress color="inherit" size={20} /> : <GroupsRoundedIcon />}
                                onClick={() => handleClickGenerate()}
                                disabled={requestInProgress}
                            >
                                Vygenerovat
                            </Button>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    )
}
